import apiClient from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import { uploadAdminFiles } from "@/lib/files.api";
import { resolveEndpoint } from "@/lib/roleAwareApi";
import type {
  LibraryItemApi,
  LibraryItemsResponse,
  LibraryItemsMeta,
  LibraryFolderItem,
  LibraryFileItem,
  LibraryItem,
  CreateFolderPayload,
  UploadDocumentParams,
} from "../types/library.types";

const LIBRARY_ITEMS_ENDPOINT = "/api/admin/library/items";
/**
 * Kursiyer (client) okuma uç noktası. /api/admin/* yalnızca personel rol tipine
 * açık olduğundan kursiyer kütüphanesi listeyi buradan okur (pentest 3.2.2).
 */
const CLIENT_LIBRARY_ITEMS_ENDPOINT = "/api/client/library/items";
const LIBRARY_FOLDERS_ENDPOINT = "/api/admin/library/items/folders";
const LIBRARY_FILES_ENDPOINT = "/api/admin/library/items/files";

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function mimeToFileType(mimeType: string, originalName?: string): string {
  if (originalName) {
    const ext = originalName.split(".").pop()?.toLowerCase();
    if (ext) return ext;
  }
  const map: Record<string, string> = {
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "docx",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "text/plain": "txt",
    "application/vnd.ms-powerpoint": "ppt",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      "pptx",
    "image/png": "png",
    "image/jpeg": "jpg",
  };
  return map[mimeType] ?? "document";
}

function mapApiItemToFolder(item: LibraryItemApi): LibraryFolderItem {
  const uploadedBy = item.createdByUser
    ? [item.createdByUser.firstName, item.createdByUser.lastName]
        .filter(Boolean)
        .join(" ")
    : undefined;
  return {
    id: item.id,
    name: item.name,
    lastUpdatedFormatted: formatDate(item.updatedAt),
    createdAt: item.createdAt,
    uploadedBy,
  };
}

function mapApiItemToFile(item: LibraryItemApi): LibraryFileItem {
  const uploadedBy = item.createdByUser
    ? [item.createdByUser.firstName, item.createdByUser.lastName]
        .filter(Boolean)
        .join(" ")
    : "";
  const name = item.file?.originalName ?? item.name;
  const fileType = item.file
    ? mimeToFileType(item.file.mimeType, item.file.originalName)
    : "document";
  const fileSize = item.file ? formatFileSize(item.file.size) : "—";
  return {
    id: item.id,
    name,
    fileType,
    uploadedBy,
    lastUpdatedFormatted: formatDate(item.updatedAt),
    fileSize,
    createdAt: item.createdAt,
    downloadUrl:
      item.downloadUrl ??
      item.file?.downloadUrl ??
      (item as { download_url?: string }).download_url ??
      (item.file as { download_url?: string } | undefined)?.download_url,
  };
}

/**
 * Kütüphane okuma kapsamı:
 * - "client": /api/client/library/items (kursiyer kütüphanesi, /dashboard/library)
 * - "admin":  /api/admin/library/items  (yönetim kütüphanesi, /dashboard/admin-library)
 * Verilmezse store'daki isStaff bayrağına göre seçilir.
 */
export type LibraryScope = "client" | "admin";

/** Sayfa basePath'inden okuma kapsamını türetir. */
export const libraryScopeFromBasePath = (
  basePath: "library" | "admin-library",
): LibraryScope => (basePath === "library" ? "client" : "admin");

const resolveLibraryReadEndpoint = (scope?: LibraryScope): string => {
  if (scope === "client") return CLIENT_LIBRARY_ITEMS_ENDPOINT;
  if (scope === "admin") return LIBRARY_ITEMS_ENDPOINT;
  return resolveEndpoint({
    user: CLIENT_LIBRARY_ITEMS_ENDPOINT,
    staff: LIBRARY_ITEMS_ENDPOINT,
  });
};

export interface GetLibraryItemsParams {
  /** Okuma kapsamı; bkz. LibraryScope */
  scope?: LibraryScope;
  parentItemId?: string;
  /** Verilmezse backend tek listede klasör + dosya döner */
  type?: "folder" | "file";
  page?: number;
  limit?: number;
  search?: string;
  updatedAfter?: string;
  /** name | createdAt | type | updatedAt — verilmezse backend updatedAt, desc kullanır */
  sortBy?: "name" | "createdAt" | "type" | "updatedAt";
  sortOrder?: "asc" | "desc";
}

export interface GetLibraryItemsResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

function parseResponseData(raw: LibraryItemsResponse): {
  items: LibraryItemApi[];
  meta: { page: number; limit: number; total: number; totalPages: number };
} {
  const data = raw.data;
  const meta = raw.meta ?? { page: 1, limit: 20, total: 0, totalPages: 0 };
  if (Array.isArray(data)) {
    const isItemArray =
      data.length === 0 || (data[0] && "type" in data[0] && "id" in data[0]);
    if (isItemArray) {
      return { items: data as LibraryItemApi[], meta };
    }
  }
  if (data && typeof data === "object" && "items" in data) {
    const d = data as { items: LibraryItemApi[]; meta?: LibraryItemsMeta };
    return {
      items: d.items ?? [],
      meta: d.meta ?? meta,
    };
  }
  return { items: [], meta };
}

function mapApiItemToItem(api: LibraryItemApi): LibraryItem {
  if (api.type === "folder") {
    return { kind: "folder", ...mapApiItemToFolder(api) };
  }
  return { kind: "file", ...mapApiItemToFile(api) };
}

export const libraryApi = {
  getItems: async (
    params: GetLibraryItemsParams,
  ): Promise<
    | GetLibraryItemsResult<LibraryFolderItem>
    | GetLibraryItemsResult<LibraryFileItem>
    | GetLibraryItemsResult<LibraryItem>
  > => {
    const query: Record<string, string | number | undefined> = {
      page: params.page ?? 1,
      limit: params.limit ?? 20,
    };
    if (params.type) query.type = params.type;
    if (params.parentItemId) query.parentItemId = params.parentItemId;
    if (params.search?.trim()) query.search = params.search.trim();
    if (params.updatedAfter) query.updatedAfter = params.updatedAfter;
    if (params.sortBy) query.sortBy = params.sortBy;
    if (params.sortOrder) query.sortOrder = params.sortOrder;

    const { data: responseData } = await apiClient.get<LibraryItemsResponse>(
      resolveLibraryReadEndpoint(params.scope),
      { params: query },
    );

    const { items, meta } = parseResponseData(responseData);

    if (params.type === "folder") {
      const data = items.map(mapApiItemToFolder);
      return { data, total: meta.total, page: meta.page, limit: meta.limit };
    }
    if (params.type === "file") {
      const data = items.map(mapApiItemToFile);
      return { data, total: meta.total, page: meta.page, limit: meta.limit };
    }
    const data = items.map(mapApiItemToItem);
    return { data, total: meta.total, page: meta.page, limit: meta.limit };
  },

  /**
   * POST /api/admin/library/items/folders – yeni klasör oluşturur.
   * @returns Oluşan klasörün id'si (içine dosya eklemek için kullanılır).
   */
  createFolder: async (
    payload: CreateFolderPayload,
  ): Promise<{ id: string }> => {
    const body: Record<string, unknown> = {
      name: payload.name,
      shareType: payload.shareType,
    };
    if (payload.parentItemId != null && payload.parentItemId !== "") {
      body.parentItemId = payload.parentItemId;
    }
    if (payload.sharedUserIds?.length)
      body.sharedUserIds = payload.sharedUserIds;
    if (payload.sharedDepartmentIds?.length)
      body.sharedDepartmentIds = payload.sharedDepartmentIds;
    const { data } = await apiClient.post<{
      data?: { id: string };
      id?: string;
    }>(LIBRARY_FOLDERS_ENDPOINT, body);
    const id = data?.data?.id ?? (data as { id?: string })?.id;
    if (!id) throw new Error(i18n.t("libraryApi.folderCreatedNoId"));
    return { id };
  },

  /**
   * Belge ekler: fileIds verilmişse doğrudan onları kullanır; yoksa önce POST /api/admin/files/upload
   * ile dosyaları yükler, dönen id'lerle POST /api/admin/library/items/files'e body gönderir.
   */
  uploadDocuments: async (params: UploadDocumentParams): Promise<void> => {
    let fileIds: string[];
    if (params.fileIds && params.fileIds.length > 0) {
      fileIds = params.fileIds;
    } else if (params.files && params.files.length > 0) {
      const uploaded = await uploadAdminFiles(params.files);
      if (uploaded.length === 0)
        throw new Error(i18n.t("libraryApi.uploadFailed"));
      fileIds = uploaded.map((f) => f.id);
    } else {
      throw new Error("files veya fileIds gerekli");
    }

    const body: Record<string, unknown> = {
      fileIds,
      shareType: params.shareType,
    };
    if (params.parentItemId != null && params.parentItemId !== "") {
      body.parentItemId = params.parentItemId;
    }
    if (params.sharedUserIds?.length) body.sharedUserIds = params.sharedUserIds;
    if (params.sharedDepartmentIds?.length)
      body.sharedDepartmentIds = params.sharedDepartmentIds;

    await apiClient.post(LIBRARY_FILES_ENDPOINT, body);
  },

  /**
   * DELETE /api/admin/library/items/{id} – Kütüphane öğesini (klasör veya dosya) siler.
   */
  deleteItem: async (id: string): Promise<void> => {
    await apiClient.delete(`${LIBRARY_ITEMS_ENDPOINT}/${id}`);
  },
};
